import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/domain/Bits";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Ticket, BadgeCheck, Users, Trophy, BarChart3, ShieldAlert,
  Palette, LogOut, Menu, Building2, Handshake, Radio, KeyRound, Vote,
} from "lucide-react";

const LEVELS = { superadmin: 100, tenant_owner: 80, admin: 60, supervisor: 50, validator: 40, analyst: 30, seller: 20 };

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, min: "analyst" },
  { to: "/vendedor", label: "Mi portal", icon: LayoutDashboard, end: true, only: "seller" },
  { to: "/admin/sorteos", label: "Sorteos", icon: Ticket, min: "admin" },
  { to: "/admin/validacion", label: "Validación de pagos", icon: BadgeCheck, min: "validator" },
  { to: "/admin/vendedores", label: "Vendedores", icon: Handshake, min: "admin" },
  { to: "/admin/usuarios", label: "Usuarios", icon: Users, min: "admin" },
  { to: "/admin/ganadores", label: "Ganadores", icon: Trophy, min: "admin" },
  { to: "/admin/encuestas", label: "Encuestas", icon: Vote, min: "admin" },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3, min: "analyst" },
  { to: "/admin/auditoria", label: "Auditoría", icon: ShieldAlert, min: "supervisor" },
  { to: "/admin/branding", label: "Configuración", icon: Palette, min: "admin" },
];

export default function AdminLayout({ children, title }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const changePw = async () => {
    if (nw.length < 6) { toast.error("La nueva contraseña debe tener al menos 6 caracteres"); return; }
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Contraseña actualizada");
      setPwOpen(false); setCur(""); setNw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cambiar");
    }
  };

  const SideNav = () => (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center border-b border-slate-800 px-5"><Logo dark /></div>
      <div className="px-4 py-3">
        <div className="flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2">
          <Building2 className="h-4 w-4 text-blue-400" />
          <div className="leading-tight">
            <div className="text-xs font-semibold text-white">{user?.tenant_name || "Empresa"}</div>
            <div className="text-[10px] text-slate-400">{user?.tenant_plan ? `Plan ${user.tenant_plan} · activo` : "Activo"}</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
        {NAV.filter((n) => (n.only ? user?.role === n.only : (LEVELS[user?.role] || 0) >= LEVELS[n.min])).map((n) => {
          const active = n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
          const Icon = n.icon;
          return (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`admin-nav-${n.label}`}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                active ? "bg-brand text-white" : "text-slate-300 hover:bg-slate-800"}`}>
              <Icon className="h-4 w-4" />{n.label}
            </Link>
          );
        })}
      </nav>
      <div className="border-t border-slate-800 p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <Avatar className="h-8 w-8"><AvatarImage src={user?.picture} /><AvatarFallback>{user?.name?.[0] || "A"}</AvatarFallback></Avatar>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate text-xs font-semibold text-white">{user?.name}</div>
            <div className="truncate text-[10px] text-slate-400">{user?.role}</div>
          </div>
          <button onClick={() => setPwOpen(true)} data-testid="change-password-btn" title="Cambiar mi contraseña" className="text-slate-400 hover:text-white"><KeyRound className="h-4 w-4" /></button>
          <button onClick={() => { logout(); nav("/"); }} data-testid="admin-logout" className="text-slate-400 hover:text-white"><LogOut className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#FAFAFB]">
      <aside className="fixed inset-y-0 left-0 hidden w-64 bg-[#0A0A0B] lg:block">{SideNav()}</aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 bg-[#0A0A0B]">{SideNav()}</div>
        </div>
      )}
      <div className="min-w-0 flex-1 lg:ml-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-2 border-b border-slate-200 bg-white/80 px-4 backdrop-blur-xl sm:px-8">
          <div className="flex min-w-0 items-center gap-2">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-menu-btn"><Menu className="h-5 w-5" /></Button>
            <h1 className="truncate text-base font-bold tracking-tight text-slate-900 sm:text-lg">{title}</h1>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Button asChild variant="outline" size="sm"><Link to="/"><Radio className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Ver sitio público</span></Link></Button>
            <Button variant="ghost" size="sm" onClick={() => { logout(); nav("/"); }} data-testid="admin-logout-top"><LogOut className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Cerrar sesión</span></Button>
          </div>
        </header>
        <div className="p-5 sm:p-8">{children}</div>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Cambiar mi contraseña</DialogTitle>
            <DialogDescription>Ingresa tu contraseña actual y la nueva.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="password" placeholder="Contraseña actual" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cp-current" />
            <Input type="password" placeholder="Nueva contraseña (mín. 6)" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="cp-new" />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPwOpen(false)}>Cancelar</Button>
            <Button onClick={changePw} className="bg-brand hover:bg-brand-hover" data-testid="cp-save">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
